'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TopbarProps {
  title: string;
  subtitle?: string;
}

export default function Topbar({ title, subtitle }: TopbarProps) {
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="h-16 bg-surface-2 border-b border-border flex items-center px-6 gap-4 sticky top-0 z-30">
      <div className="flex-1 min-w-0">
        <h1 className="text-base font-semibold text-foreground leading-tight truncate">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground leading-tight truncate">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-2">
        {/* Search */}
        <div className={`flex items-center gap-2 transition-all duration-200 ${searchOpen ? 'w-64' : 'w-auto'}`}>
          {searchOpen ? (
            <div className="flex items-center gap-2 bg-input border border-border rounded-lg px-3 py-1.5 w-full">
              <Icon name="MagnifyingGlassIcon" size={14} className="text-muted-foreground shrink-0" />
              <input
                autoFocus
                type="text"
                placeholder="Search BVOs, twins, processes..."
                className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none flex-1 min-w-0"
                onBlur={() => setSearchOpen(false)}
              />
              <kbd className="text-2xs text-muted-foreground bg-muted px-1 py-0.5 rounded font-mono">Esc</kbd>
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 bg-input border border-border rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-150"
            >
              <Icon name="MagnifyingGlassIcon" size={14} />
              <span className="hidden sm:inline text-xs">Search</span>
              <kbd className="hidden sm:inline text-2xs bg-muted px-1 py-0.5 rounded font-mono">⌘K</kbd>
            </button>
          )}
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150">
          <Icon name="BellIcon" size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-negative rounded-full border-2 border-surface-2" />
        </button>

        {/* Date indicator */}
        <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-lg border border-border">
          <Icon name="CalendarIcon" size={12} className="shrink-0" />
          <span>Jun 7, 2026</span>
        </div>

        {/* User */}
        <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-all duration-150">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-xs font-semibold text-primary-foreground">
            AK
          </div>
          <div className="hidden md:flex flex-col items-start">
            <span className="text-xs font-medium text-foreground leading-tight">Anika Kowalski</span>
            <span className="text-2xs text-muted-foreground leading-tight">COO</span>
          </div>
          <Icon name="ChevronDownIcon" size={12} className="text-muted-foreground hidden md:block" />
        </button>
      </div>
    </header>
  );
}